import * as R from 'ramda'
import {
  compose,
  always,
  identity,
  isNil,
  isEmpty,
  ifElse,
  flatten,
  map,
  concat,
  pick,
} from 'ramda'
import { applyThousandSeparator, pricePerNoti } from 'commons/number'
import { PlanId, isPhysicalWebStarterPackByPlanId } from 'commons/plan'

const flatMap = (fn) => compose(flatten, map(fn))

const GroupPlan = {
  MOM_AND_POP: 'Mom & Pop',
  SMB: 'SMB',
  LARGE_BUSINESS: 'Large Business',
  DEFAULT: 'DEFAULT',
}

export const GroupPlanLabel = {
  [GroupPlan.MOM_AND_POP]: 'registerForm.plans.momAndPop',
  [GroupPlan.SMB]: 'registerForm.plans.SMB',
  [GroupPlan.LARGE_BUSINESS]: 'registerForm.plans.largeBusiness',
}

const orderedGroupPlanNames = [
  GroupPlan.DEFAULT,
  GroupPlan.MOM_AND_POP,
  GroupPlan.SMB,
  GroupPlan.LARGE_BUSINESS,
]

const searchParams = (search) => new URLSearchParams(search)
const get = (name) => (searchParams) => searchParams.get(name)

export const planCodeBySearchParams = compose(
  ifElse(R.anyPass([isNil, isEmpty]), always(undefined), identity),
  get('plan'),
  searchParams,
)

const flattenPlans = flatMap(({ title, id, items }) =>
  concat(
    [{ type: 'section', title, id }],
    R.map(mapPlans(items), orderedGroupPlanNames),
  ),
)
export const NewPlans = [PlanId.PHYSICAL_WEB_STARTER_PACK]

// const isFreePlan = R.o(R.equals(PlanId.FREE_TRIAL), R.prop('planId'));
const isFreeTierPlan = R.o(R.equals(PlanId.FREE_TIER), R.prop('planId'))
const isNewPlan = R.o(R.includes(R.__, NewPlans), R.prop('planId'))
const isMonthlyPlan = R.o(R.test(/^monthly/), R.prop('planId'))
const isAnnualPlan = R.o(R.test(/^annual/), R.prop('planId'))

/**
 * Lens s [a] -> s -> (a -> b) -> a -> s
 * takes lens of whole s to part [a] where a is element of list
 * and takes whole s
 * and tekes transform function from part a to b
 * and takes a to append to s of specified lens
 * returns new s
 */
const transformAppendByLens = (lens, input, transform) =>
  R.compose(R.over(lens, R.__, input), R.append, transform)

export const planTypes = {
  free: 'free',
  free_tier: 'free_tier',
  monthly: 'monthly',
  annual: 'annual',
  unhandler: 'unhandler',
}

export const planDescription = (formatMessage, planType, plan) => {
  if (isPhysicalWebStarterPackByPlanId(R.prop('planId', plan))) {
    return formatMessage(
      {
        id: 'planAndBilling.physicalWebStarterSubtitle',
      },
      {
        price: plan.price,
        quantity: plan.notesQuantity,
      },
    )
  }

  switch (planType) {
    case planTypes.free_tier:
      return formatMessage(
        {
          id: 'planAndBilling.yourPlan.changePlan.changeToFreeTier.subTitle',
        },
        {
          quantity: plan.notesQuantity,
        },
      )
    case planTypes.monthly:
      return formatMessage(
        {
          id: 'planAndBilling.monthlySubtitle',
        },
        {
          price: applyThousandSeparator(plan.price),
          quantity: applyThousandSeparator(plan.notesQuantity),
          pricePerNoti: pricePerNoti(plan.price, plan.notesQuantity),
        },
      )
    case planTypes.annual:
      return formatMessage(
        {
          id: 'planAndBilling.annualSubtitle',
        },
        {
          price: applyThousandSeparator(plan.price),
          quantity: applyThousandSeparator(plan.notesQuantity),
          pricePerNoti: pricePerNoti(plan.price, plan.notesQuantity),
        },
      )
    default:
      return 'unhandler case'
  }
}

const getGroupByPlan = R.compose(
  R.cond([
    [R.includes(GroupPlan.MOM_AND_POP), R.always(GroupPlan.MOM_AND_POP)],
    [R.includes(GroupPlan.SMB), R.always(GroupPlan.SMB)],
    [R.includes(GroupPlan.LARGE_BUSINESS), R.always(GroupPlan.LARGE_BUSINESS)],
    [R.T, R.always(GroupPlan.DEFAULT)],
  ]),
  R.prop('name'),
)

const planItem = R.curry((formatMessage, planType, plan) => ({
  ...plan,
  id: plan.planId,
  title: plan.name,
  price: plan.price,
  group: getGroupByPlan(plan),
  description: planDescription(formatMessage, planType, plan),
}))

export const SectionPlanId = {
  NEW_PACKAGE: 'NEW_PACKAGE',
  CHANGE_TO_FREE_TIER: 'CHANGE_TO_FREE_TIER',
  MONTHLY: 'MONTHLY',
  ANNUAL: 'ANNUAL',
}

const getPlanTypeByFrequency = (frequency) => {
  return {
    12: planTypes.annual,
    1: planTypes.monthly,
  }[frequency]
}

const getInitialSections = (formatMessage) => [
  {
    title: SectionPlanId.NEW_PACKAGE,
    id: SectionPlanId.NEW_PACKAGE,
    items: [],
  },
  {
    title: formatMessage({
      id: 'planAndBilling.yourPlan.changePlan.changeToFreeTier',
    }),
    id: 'CHANGE_TO_FREE_TIER',
    items: [],
  },
  {
    title: formatMessage({
      id: 'registerForm.monthlyTitle',
    }),
    id: 'MONTHLY',
    items: [],
  },
  {
    title: formatMessage({
      id: 'registerForm.annualTitle',
    }),
    id: 'ANNUAL',
    items: [],
  },
]

export const getTransformPlans = (plans, formatMessage) => {
  let list = R.reduce(
    (acc, plan) =>
      R.cond([
        [
          isNewPlan,
          transformAppendByLens(
            R.lensPath([0, 'items']),
            acc,
            planItem(formatMessage, getPlanTypeByFrequency(plan.frequency)),
          ),
        ],
        [
          isFreeTierPlan,
          transformAppendByLens(
            R.lensPath([1, 'items']),
            acc,
            planItem(formatMessage, planTypes.free_tier),
          ),
        ],
        [
          isMonthlyPlan,
          transformAppendByLens(
            R.lensPath([2, 'items']),
            acc,
            planItem(formatMessage, planTypes.monthly),
          ),
        ],
        [
          isAnnualPlan,
          transformAppendByLens(
            R.lensPath([3, 'items']),
            acc,
            planItem(formatMessage, planTypes.annual),
          ),
        ],
        [R.T, R.always(acc)],
      ])(plan),
    getInitialSections(formatMessage),
    plans.data,
  )
  return list
}

const groupItemsByGroup = R.evolve({ items: R.groupBy(R.prop('group')) })

const parsePlan = (plan) => ({
  type: 'plan',
  ...pick(
    ['id', 'price', 'group', 'title', 'description', 'notesQuantity'],
    plan,
  ),
})

const mapPlans = (items) => (groupPlanName) => {
  const plans = R.propOr([], groupPlanName, items)

  if (R.isEmpty(plans)) return []

  if (R.equals(groupPlanName, GroupPlan.DEFAULT)) return R.map(parsePlan, plans)

  const initialPlans = [
    {
      type: 'group',
      title: groupPlanName,
      key: `${groupPlanName}${R.path([0, 'id'], plans)}`,
    },
  ]

  const reducePlan = (acc, plan) => {
    return [...acc, parsePlan(plan)]
  }

  return R.reduce(reducePlan, initialPlans, plans)
}

export const plansToSelect = R.compose(
  flattenPlans,
  R.map(groupItemsByGroup),
  getTransformPlans,
)
